/* eslint-disable max-len */
/* eslint-disable max-classes-per-file */
import { Draft } from 'immer';
import { CustomizationValue } from './CustomizationValue';
import {
  TreeOptionSelection,
  TreeOptionSelections,
} from '../../common/libraries/modelConfiguration/data/optionsSelection';
import { PriceData } from '../../common/libraries/fileFormats/catalog/catalog';
import { ItemMeasuresInfo } from '../../common/libraries/fileFormats/ItemMeasuresInfo';
import { CustomAttributeValue, CustomAttributeValueDTO, toDTO } from '../../common/libraries/customAttributes/customAttributeValue';

export class CartItem {

  public collectionName : string = '';
  public objectName : string = '';
  public variant : TreeOptionSelections = [];
  public lastVariantSelected : string = '';
  public customizations : Array<CustomizationValue> = [];
  public quantity : number = 0;
  public basePrice : number = 0;
  public totalPrice : number = 0;
  public extrasPrice: number = 0;
  public cartIndex : number = 0;
  public catalogModelKey: string = '';
  public optionsPrice : Array<PriceData> = [];
  public objectDetails : Array<ItemMeasuresInfo> = [];
  public objectCustomAttributes : CustomAttributeValueDTO[] = [];
  public orderLineCustomAttributes : CustomAttributeValueDTO[] = [];
  public screenshotId : string = '';
  public screenshotBase64 : string = '';
}

export class CartItemHelper {

  static Create(
    collectionName : string,
    objectName : string,
    variant : TreeOptionSelections | undefined,
    lastVariantSelected: string,
    customizations : Array<CustomizationValue>,
    quantity : number,
    basePrice: number,
    totalPrice: number,
    extrasPrice: number,
    catalogModelKey: string,
    optionsPrice : Array<PriceData>,
    objectDetails: Array<ItemMeasuresInfo>,
    objectCustomAttributes: CustomAttributeValueDTO[],
    orderLineCustomAttributes: CustomAttributeValueDTO[],
    screenshotId: string,
  ): CartItem {

    return {
      collectionName,
      objectName,
      variant: variant ? [...variant] : [],
      lastVariantSelected,
      customizations: [...customizations],
      quantity,
      basePrice,
      totalPrice,
      extrasPrice,
      cartIndex: 0,
      catalogModelKey,
      optionsPrice: [...optionsPrice],
      objectDetails: [...objectDetails],
      objectCustomAttributes: [...objectCustomAttributes.map((a) => toDTO(a))],
      orderLineCustomAttributes: [...orderLineCustomAttributes.map((a) => toDTO(a))],
      screenshotId,
      screenshotBase64: '',
    };
  }

  static DuplicateItemCart(cartItem : CartItem) : CartItem {
    return CartItemHelper.Create(
      cartItem.collectionName,
      cartItem.objectName,
      cartItem.variant,
      cartItem.lastVariantSelected,
      cartItem.customizations,
      cartItem.quantity,
      cartItem.basePrice,
      cartItem.totalPrice,
      cartItem.extrasPrice,
      cartItem.catalogModelKey,
      cartItem.optionsPrice,
      cartItem.objectDetails,
      cartItem.objectCustomAttributes,
      cartItem.orderLineCustomAttributes,
      cartItem.screenshotId,
    );
  }

  static GetCollectionName(cartItem : CartItem) {
    return cartItem.collectionName;
  }

  static GetObjectName(cartItem : CartItem) {
    return cartItem.objectName;
  }

  static GetVariant(cartItem : CartItem) {
    return cartItem.variant;
  }

  static GetCustomizations(cartItem : CartItem | Draft<CartItem>) {
    return cartItem.customizations;
  }

  static SetQuantity(cartItem : CartItem | Draft<CartItem>, quantity : number) {
    // eslint-disable-next-line no-param-reassign
    cartItem.quantity = quantity;
  }

  static GetQuantity(cartItem : CartItem | Draft<CartItem>) {
    return Number(cartItem.quantity);
  }

  static SetCartIndex(cartItem : CartItem, index : number) {
    // eslint-disable-next-line no-param-reassign
    cartItem.cartIndex = index;
  }

  static SetCartScreenshot(cartItem : CartItem, screenshotBase64 : string) {
    // eslint-disable-next-line no-param-reassign
    cartItem.screenshotBase64 = screenshotBase64;
  }

  static GetCartIndex(cartItem : CartItem) {
    return cartItem.cartIndex;
  }

  static AreSimilar(cartItemLeft : CartItem | Draft<CartItem>, cartItemRight : CartItem) {
    // Check collection and object names
    if (
      cartItemLeft.collectionName !== cartItemRight.collectionName
      || cartItemLeft.objectName !== cartItemRight.objectName
    ) {
      return false;
    }

    // Check if the selected parts are the same

    cartItemLeft.variant.sort(CartItemHelper.CompareVariantGroup);
    cartItemRight.variant.sort(CartItemHelper.CompareVariantGroup);

    const numVariantPartsLeft = cartItemLeft.variant.length;
    const numVariantPartsRight = cartItemRight.variant.length;

    if (numVariantPartsLeft !== numVariantPartsRight) {
      // This is likely due to a problem in the application.
      // Since this is the same object, the number of variants in each should match
      return false;
    }

    for (
      let curVariantPart = 0;
      curVariantPart < numVariantPartsLeft;
      curVariantPart++
    ) {
      if (
        cartItemLeft.variant[curVariantPart].groupID
        !== cartItemRight.variant[curVariantPart].groupID
      ) {
        // This is likely due to a problem in the application.
        // Since this is the same object, and the variants are sorted
        // The group IDs should match
        return false;
      }

      if (
        cartItemLeft.variant[curVariantPart].optionID
        !== cartItemRight.variant[curVariantPart].optionID
      ) {
        return false;
      }
    }

    // Check customizations

    const customizationsItemLeft = CartItemHelper.GetCustomizations(cartItemLeft);
    const customizationsItemRight = CartItemHelper.GetCustomizations(cartItemRight);

    const numCustomizationsItemLeft = customizationsItemLeft
      ? customizationsItemLeft.length
      : 0;
    const numCustomizationsItemRight = customizationsItemRight
      ? customizationsItemRight.length
      : 0;

    if (numCustomizationsItemLeft !== numCustomizationsItemRight) {
      return false;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const customizationLeft of customizationsItemLeft) {
      const leftPartIdentifier = CustomizationValue.getPartIdentifier(customizationLeft);
      const leftCustomizationID = CustomizationValue.getCustomizationID(customizationLeft);

      // Check if we can find the same customization in the right

      const customizationRight = customizationsItemRight.find(
        (rightCustomization) => {
          return (
            CustomizationValue.getPartIdentifier(rightCustomization)
              === leftPartIdentifier
            && CustomizationValue.getCustomizationID(rightCustomization)
              === leftCustomizationID
          );
        },
      );

      if (customizationRight === undefined) {
        // This item could not be found. Not the same item
        return false;
      }

      // Compare the data
      // Because the data can be a lot of different things, compare it as a string
      const leftCustomizationData = CustomizationValue.getData(customizationLeft);
      const leftCustomizationDataAsString = leftCustomizationData
        ? JSON.stringify(leftCustomizationData)
        : 'undefined';

      const rightCustomizationData = CustomizationValue.getData(customizationRight);
      const rightCustomizationDataAsString = rightCustomizationData
        ? JSON.stringify(rightCustomizationData)
        : 'undefined';

      if (
        leftCustomizationDataAsString.normalize()
        !== rightCustomizationDataAsString.normalize()
      ) {
        return false;
      }
    }

    // Compare selected measures
    if (!CartItemHelper.CompareItemAttributes(cartItemLeft.objectCustomAttributes, cartItemRight.objectCustomAttributes)) {
      return false;
    }

    if (JSON.stringify(cartItemLeft.objectDetails) !== JSON.stringify(cartItemRight.objectDetails)) {
      return false;
    }

    const bHasDiff = cartItemLeft.objectCustomAttributes.some((cVLeft) => {
      const cVRight = cartItemRight.objectCustomAttributes.find(
        (cV) => cV.attributeKey === cVLeft.attributeKey,
      );
      return (
        !cVRight
    || cVRight.attributeValue !== cVLeft.attributeValue
    || cVRight.attributeQuantity !== cVLeft.attributeQuantity
      );
    });

    return !bHasDiff;
  }

  static CompareVariantGroup(
    partLeft : TreeOptionSelection,
    partRight : TreeOptionSelection,
  ) {
    if (partLeft.groupID < partRight.groupID) {
      return -1;
    }

    if (partLeft.groupID > partRight.groupID) {
      return 1;
    }
    return 0;
  }

  static CompareItemAttributes(
    leftA: CustomAttributeValue[],
    rightA: CustomAttributeValue[],
  ) : boolean {
    // Create a shallow copy of the arrays and sort the copies
    const sortedLeftA = [...leftA].sort((a, b) => a.attributeKey.localeCompare(b.attributeKey));
    const sortedRightA = [...rightA].sort((a, b) => a.attributeKey.localeCompare(b.attributeKey));
    if (JSON.stringify(sortedLeftA) !== JSON.stringify(sortedRightA)) {
      return false;
    }
    return true;
  }

}
